// apps/dashboard/src/components/branding-provider.tsx
// Reads the tenant's brandingConfig and applies white-label overrides to the DOM:
//   - primaryColor → CSS custom property --primary (and --ring) on <html>
// Renders children immediately; CSS override is applied as a side-effect.

import { useBranding } from '@/hooks/use-branding';
import { type ReactNode, useEffect } from 'react';

interface Props {
  children: ReactNode;
}

/**
 * Converts a hex colour (#RRGGBB) to the HSL string format expected by the
 * Tailwind v4 CSS-variable theme ("H S% L%").
 */
function hexToHslString(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  const r = Number.parseInt(result[1] as string, 16) / 255;
  const g = Number.parseInt(result[2] as string, 16) / 255;
  const b = Number.parseInt(result[3] as string, 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function BrandingProvider({ children }: Props) {
  const { data } = useBranding();
  const config = data?.brandingConfig;

  useEffect(() => {
    const root = document.documentElement;

    if (config?.primaryColor) {
      const hsl = hexToHslString(config.primaryColor);
      if (hsl) {
        // Override the Tailwind CSS-variable theme tokens used by all components
        root.style.setProperty('--primary', hsl);
        root.style.setProperty('--ring', hsl);
        root.style.setProperty('--brand', hsl);
        // Also derive a lighter version for backgrounds/hover states
        const parts = hsl.split(' ');
        const h = parts[0];
        const s = parts[1];
        const lStr = parts[2];
        if (h && s && lStr) {
          const l = Number.parseInt(lStr);
          const lightL = Math.min(l + 40, 95);
          root.style.setProperty('--primary-foreground', '0 0% 100%');
          root.style.setProperty('--primary-muted', `${h} ${s} ${lightL}%`);
        }
      }
    } else {
      // Remove overrides when branding is cleared — revert to design-system defaults
      root.style.removeProperty('--primary');
      root.style.removeProperty('--ring');
      root.style.removeProperty('--brand');
      root.style.removeProperty('--primary-foreground');
      root.style.removeProperty('--primary-muted');
    }
  }, [config?.primaryColor]);

  return <>{children}</>;
}
